import { CheckIcon, Combobox, Tooltip, Pill, PillsInput, ScrollArea, useCombobox } from '@mantine/core';

interface TagOverflowCounterMultiSelectProps {
  /** 标签组数据 */
  data: { group: string; items: string[] }[];
  /** 当前选中的标签值 */
  value: string[];
  /** 选择变化回调 */
  onChange: (value: string[]) => void;
  /** 占位符文本 */
  placeholder?: string;
  /** 是否启用搜索 */
  searchable?: boolean;
  /** 下拉框最大高度 */
  maxDropdownHeight?: number | string;
  /** 显示溢出计数器前最大显示的标签数量，超过此数量显示 +N */
  maxVisibleItems?: number;
}

export function TagOverflowCounterMultiSelect({
  data,
  value,
  onChange,
  placeholder,
  searchable = true,
  maxDropdownHeight = 300,
  maxVisibleItems = 5,
}: TagOverflowCounterMultiSelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  const handleValueSelect = (val: string) =>
    onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);

  const handleValueRemove = (val: string) => onChange(value.filter((v) => v !== val));

  // 当选择超过maxVisibleItems个时，只显示前(maxVisibleItems-1)个，然后显示 "+N" 标签
  const displayValues = (() => {
    if (value.length <= maxVisibleItems) {
      return value.map((item) => (
        <Pill key={item} onRemove={() => handleValueRemove(item)}>
          <Tooltip label={item}>
            <span className="inline-block max-w-24 truncate">{item}</span>
          </Tooltip>
        </Pill>
      ));
    }

    // 显示前(maxVisibleItems-1)个
    const visibleCount = maxVisibleItems - 1;
    const visibleValues = value.slice(0, visibleCount).map((item) => (
      <Pill key={item} onRemove={() => handleValueRemove(item)}>
        <Tooltip label={item}>
          <span className="inline-block max-w-36 truncate">{item}</span>
        </Tooltip>
      </Pill>
    ));

    // 添加 "+N" 标签
    const remainingCount = value.length - visibleCount;
    visibleValues.push(
      <Pill key="overflow-counter" withRemoveButton={false}>
        +{remainingCount}
      </Pill>
    );

    return visibleValues;
  })();

  // 生成选项
  const options = data.map((group) => (
    <Combobox.Group
      key={group.group}
      label={
        <span className="block max-w-[50%] truncate" title={group.group}>
          {group.group}
        </span>
      }
    >
      {group.items.map((item) => (
        <Combobox.Option value={item} key={item} active={value.includes(item)}>
          <div className="flex items-center">
            {value.includes(item) ? <CheckIcon size={12} className="mr-2" /> : null}
            <span>{item}</span>
          </div>
        </Combobox.Option>
      ))}
    </Combobox.Group>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleValueSelect}
      middlewares={{ flip: true, shift: false }}
      classNames={{ groupLabel: 'overflow-hidden' }}
    >
      <Combobox.DropdownTarget>
        <PillsInput
          pointer
          onClick={() => combobox.toggleDropdown()}
          style={{
            maxWidth: 250,
          }}
          styles={{
            input: {
              borderColor: 'rgb(228, 232, 255)',
            },
          }}
        >
          <Pill.Group>
            {displayValues}

            {searchable && (
              <Combobox.EventsTarget>
                <PillsInput.Field
                  placeholder={placeholder}
                  onBlur={() => combobox.closeDropdown()}
                  onKeyDown={(event) => {
                    if (event.key === 'Backspace' && value.length > 0) {
                      event.preventDefault();
                      handleValueRemove(value[value.length - 1]);
                    }
                  }}
                />
              </Combobox.EventsTarget>
            )}
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <ScrollArea.Autosize mah={maxDropdownHeight} mih={40} type="always" scrollbars="y">
          <Combobox.Options>{options}</Combobox.Options>
        </ScrollArea.Autosize>
      </Combobox.Dropdown>
    </Combobox>
  );
}
